import React from 'react';
import { Text, View, Image } from '@react-pdf/renderer';
import { pdfSharedStyles } from '../../../pdfSharedStyles';
import { TFunction } from 'i18next';
import { IGeogridData, IGeogridItem } from '../../../../../api/IApiResponse';
import { ProductDataService } from '../../../../../services/ProductDataService';
import { getTensileAtStrain } from '../../../../../services/TensileAtStrength';
import { calculateGeogridRequiredStrengthUnified, calculateGeogridStrengthBreakdownUnified } from '../../../../../services/GeogridTtotService';
import * as symbols from '../../../../../enum/symbols';
import { ensureFontsRegistered } from '../../../shared/PdfFontRegistry';
import { isEmptyUnit } from '../../shared/pdfUnitMerge';

// Funzione di parsing base per <br> e <strong>
function parseHtmlToPdfText(html: string, style?: any) {
    // Sostituisce <br> con \n
    const withBreaks = html.replace(/<br\s*\/??\s*>/gi, '\n');
    // Regex per trovare <strong>...</strong>
    const regex = /(<strong>(.*?)<\/strong>)/gi;
    const result: React.ReactElement[] = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = regex.exec(withBreaks)) !== null) {
        // Testo prima del tag
        if (match.index > lastIndex) {
            const text = withBreaks.substring(lastIndex, match.index);
            if (text) result.push(<Text key={key++} style={style}>{text}</Text>);
        }
        // Testo in grassetto
        const boldText = match[2];
        if (boldText) result.push(
            <Text
                key={key++}
                style={{ fontFamily: 'DejaVuSans', fontWeight: 'bold', ...style }}
            >
                {boldText}
            </Text>
        );
        lastIndex = regex.lastIndex;
    }
    // Testo dopo l'ultimo tag
    if (lastIndex < withBreaks.length) {
        const text = withBreaks.substring(lastIndex);
        if (text) result.push(<Text key={key++} style={style}>{text}</Text>);
    }
    return result;
}

interface PavedGeoGridCoreProps {
    geoGridData?: IGeogridData;
    geogridImage?: string;
    projectData: any;
    t: TFunction;
    showInputData?: boolean;
    showGeoGridImage?: boolean;
    showCourseResults?: boolean;
    customTitle?: string;
}

export const PavedGeoGridCore: React.FC<PavedGeoGridCoreProps> = ({
    geoGridData,
    geogridImage,
    projectData,
    t,
    showInputData = true,
    showGeoGridImage = true,
    showCourseResults = true,
    customTitle,
}) => {
    const unitSystem = projectData?.unit || 'Metric';

    // Extract GeoGrid course data
    const baseThickness = geoGridData?.geogridBaseCourse?.thickness ?? 0;
    const subbaseThickness = geoGridData?.geogridSubBaseCourse?.thickness ?? 0;
    const baseGeogrids = geoGridData?.geogridBaseCourse?.geogrids ?? [];
    const subbaseGeogrids = geoGridData?.geogridSubBaseCourse?.geogrids ?? [];
    const tipoGeoGrid = geoGridData?.type;
    const projectGeoGridData = projectData?.projectInfo?.data?.geoGrid;

    // Helper function to get unit based on system
    const getUnit = (metricUnit: string, imperialUnit: string) => {
        return unitSystem === 'Imperial' ? imperialUnit : metricUnit;
    };

    // Helper to format numbers to 2 decimal places
    const formatValue = (value: unknown): string => {
        if (typeof value === 'number') {
            return value.toFixed(2);
        }
        return String(value);
    };

    // Direct conversion factors (from conversion/index.ts)
    const CONVERSION_FACTORS: Record<string, number> = {
        'm_ft': 3.28084,
        'kN_lbf': 224.8089,
        'kPa_psi': 0.145038,
        'kPa_psf': 20.8854,
        'kN/m³_pcf': 6.36588,
        'kN/m_lbf/ft': 68.521959,
        'MPa_psi': 145.038,
    };

    // Helper function to get conversion factor
    const getConversionFactor = (metricUnit: string, imperialUnit: string): number => {
        const key = `${metricUnit}_${imperialUnit}`;
        return CONVERSION_FACTORS[key] || 1;
    };

    // Helper for consistent conversion and formatting for Imperial units
    // geoGridData values are stored in Metric, need conversion when Imperial
    const convertInputValue = (value: number | undefined | null, fieldName: string, metricUnit: string, imperialUnit: string): string => {
        if (typeof value !== 'number') return 'N/A';
        if (unitSystem === 'Imperial') {
            const factor = getConversionFactor(metricUnit, imperialUnit);
            const converted = value * factor;
            return converted.toFixed(2);
        }
        return value.toFixed(2);
    };

    // Helper to convert numeric values (for calculated results like strength)
    const convertNumericValue = (value: number | null | undefined, metricUnit: string, imperialUnit: string): string => {
        if (value === null || value === undefined) return 'N/A';
        if (unitSystem === 'Imperial') {
            const factor = getConversionFactor(metricUnit, imperialUnit);
            const converted = value * factor;
            return converted.toFixed(2);
        }
        return value.toFixed(2);
    };

    return (
        <>
            {/* Title */}
                <>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.projectInfoProjectType]}>GROUNDMAC - Road Paved</Text>
                    <View style={pdfSharedStyles.spacer0} />
                    <Text style={pdfSharedStyles.projectInfoSubtitle}>{parseHtmlToPdfText(t('paved_GeoGrid_project_info_desc_pdf') || 'Project Information')}</Text>
                </>


            {/* Input Data Summary Section */}
            {showInputData && (
                <View style={pdfSharedStyles.section}>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title, { textAlign: 'left' }]}>{t('input_data_summary')}</Text>
                    <View style={pdfSharedStyles.spacer0} />
                    <View style={pdfSharedStyles.innSection}>
                        {/* Load Table */}
                        {geoGridData?.load && (
                            <PdfTable title={t('load')} t={t}>
                                {tipoGeoGrid?.geogridType === 'Rectangular' ? (
                                    <>
                                        <PdfRow label={t('load.deckHalfWidth')} symbol="B" value={convertInputValue(geoGridData.load.deckHalfWidth, 'deckHalfWidth', 'm', 'ft')} unit={getUnit('m', 'ft')} condition={geoGridData.load.deckHalfWidth !== undefined} />
                                        <PdfRow label={t('load.deckHalfLength')} symbol="L" value={convertInputValue(geoGridData.load.deckHalfLength, 'deckHalfLength', 'm', 'ft')} unit={getUnit('m', 'ft')} condition={geoGridData.load.deckHalfLength !== undefined} />
                                        <PdfRow label={t('load.uniformLoad')} symbol="q" value={convertInputValue(geoGridData.load.uniformLoad, 'uniformLoad', 'kPa', 'psf')} unit={getUnit('kPa', 'psf')} condition={geoGridData.load.uniformLoad !== undefined} />
                                    </>
                                ) : (
                                    <>
                                        <PdfRow label={t('load.designAxleLoad')} symbol="W" value={convertInputValue(geoGridData.load.designAxleLoad ?? geoGridData.load.axleLoad, 'designAxleLoad', 'kN', 'lbf')} unit={getUnit('kN', 'lbf')} condition={geoGridData.load.designAxleLoad !== undefined || geoGridData.load.axleLoad !== undefined} />
                                        <PdfRow label={t('load.tireInflationPressure')} symbol="p" value={convertInputValue(geoGridData.load.tireInflationPressure, 'tireInflationPressure', 'kPa', 'psi')} unit={getUnit('kPa', 'psi')} condition={geoGridData.load.tireInflationPressure !== undefined} />
                                        <PdfRow label={t('load.equivalentStandardAxleLoad')} symbol={symbols.sym_pa} value={convertInputValue(geoGridData.load.equivalentStandardAxleLoad, 'equivalentStandardAxleLoad', 'kN', 'lbf')} unit={getUnit('kN', 'lbf')} condition={geoGridData.load.equivalentStandardAxleLoad !== undefined} />
                                    </>
                                )}
                            </PdfTable>
                        )}

                        {/* Truck Load Table */}
                        {geoGridData?.truck && (
                            <PdfTable title={t('truck')} t={t}>
                                <PdfRow label={t('truck.wheelRadius')} symbol={symbols.sym_rw} value={convertInputValue(geoGridData.truck.wheelRadius, 'wheelRadius', 'm', 'ft')} unit={getUnit('m', 'ft')} condition={geoGridData.truck.wheelRadius !== undefined} />
                                <PdfRow label={t('truck.wheelWidth')} symbol="b" value={convertInputValue(geoGridData.truck.wheelWidth, 'wheelWidth', 'm', 'ft')} unit={getUnit('m', 'ft')} condition={geoGridData.truck.wheelWidth !== undefined} />
                                <PdfRow label={t('truck.wheelLoad')} symbol="P" value={convertInputValue(geoGridData.truck.wheelLoad, 'wheelLoad', 'kN', 'lbf')} unit={getUnit('kN', 'lbf')} condition={geoGridData.truck.wheelLoad !== undefined} />
                                <PdfRow label={t('truck.tirePressure')} symbol={symbols.sym_pw} value={convertInputValue(geoGridData.truck.tirePressure, 'tirePressure', 'kPa', 'psi')} unit={getUnit('kPa', 'psi')} condition={geoGridData.truck.tirePressure !== undefined} />
                                <PdfRow label={t('truck.thickness')} symbol="h*" value={convertInputValue(geoGridData.truck.thickness, 'thickness', 'm', 'ft')} unit={getUnit('m', 'ft')} condition={geoGridData.truck.thickness !== undefined} />
                            </PdfTable>
                        )}

                        {/* Surface Layer Table - only if thickness > 0 */}
                        {geoGridData?.surfaceLayer &&
                            typeof geoGridData.surfaceLayer === 'object' &&
                            'thickness' in geoGridData.surfaceLayer &&
                            Number(geoGridData?.surfaceLayer?.thickness || 0) > 0 && (
                            <PdfTable title={t('surfaceLayer')} t={t}>
                                <PdfRow label={t('surfaceLayer.thickness')} symbol="h" value={convertInputValue(geoGridData.surfaceLayer.thickness, 'surfaceThickness', 'm', 'ft')} unit={getUnit('m', 'ft')} condition={geoGridData.surfaceLayer.thickness !== undefined} />
                                <PdfRow label={t('surfaceLayer.unitWeight')} symbol="γ" value={convertInputValue(geoGridData.surfaceLayer.unitWeight, 'surfaceUnitWeight', 'kN/m³', 'pcf')} unit={getUnit('kN/m³', 'pcf')} condition={geoGridData.surfaceLayer.unitWeight !== undefined} />
                                <PdfRow label={t('surfaceLayer.frictionAngle')} symbol="φ" value={formatValue(geoGridData.surfaceLayer.frictionAngle)} unit="°" condition={geoGridData.surfaceLayer.frictionAngle !== undefined} />
                                <PdfRow label={t('surfaceLayer.loadSpreadingAngle')} symbol="α" value={formatValue(geoGridData.surfaceLayer.loadSpreadingAngle)} unit="°" condition={geoGridData.surfaceLayer.loadSpreadingAngle !== undefined} />
                            </PdfTable>
                        )}

                        {/* Base Course Table */}
                        {geoGridData?.baseCourse && baseThickness > 0 && (
                            <PdfTable title={t('baseCourse')} t={t}>
                                <PdfRow label={t('baseCourse.unitWeight')} symbol={symbols.sym_yb} value={convertInputValue(geoGridData.baseCourse.unitWeight, 'baseUnitWeight', 'kN/m³', 'pcf')} unit={getUnit('kN/m³', 'pcf')} condition={geoGridData.baseCourse.unitWeight !== undefined} />
                                <PdfRow label={t('baseCourse.frictionAngle')} symbol={symbols.sym_phib} value={formatValue(geoGridData.baseCourse.frictionAngle)} unit="°" condition={geoGridData.baseCourse.frictionAngle !== undefined} />
                                <PdfRow label={t('baseCourse.loadSpreadingAngle')} symbol={symbols.sym_alphab} value={formatValue(geoGridData.baseCourse.loadSpreadingAngle)} unit="°" condition={geoGridData.baseCourse.loadSpreadingAngle !== undefined} />
                                <PdfRow label={t('baseCourse.cohesion')} symbol={symbols.sym_cb} value={convertInputValue(geoGridData.baseCourse.cohesion, 'baseCohesion', 'kPa', 'psf')} unit={getUnit('kPa', 'psf')} condition={geoGridData.baseCourse.cohesion !== undefined} />
                            </PdfTable>
                        )}

                        {/* SubBase Course Table */}
                        <View break style={{ width: '100%' }} />
                        {geoGridData?.subBaseCourse && subbaseThickness > 0 && (
                            <PdfTable title={t('subBaseCourse')} t={t}>
                                <PdfRow label={t('subBaseCourse.unitWeight')} symbol={symbols.sym_ys} value={convertInputValue(geoGridData.subBaseCourse.unitWeight, 'subbaseUnitWeight', 'kN/m³', 'pcf')} unit={getUnit('kN/m³', 'pcf')} condition={geoGridData.subBaseCourse.unitWeight !== undefined} />
                                <PdfRow label={t('subBaseCourse.frictionAngle')} symbol={symbols.sym_phis} value={formatValue(geoGridData.subBaseCourse.frictionAngle)} unit="°" condition={geoGridData.subBaseCourse.frictionAngle !== undefined} />
                                <PdfRow label={t('subBaseCourse.loadSpreadingAngle')} symbol={symbols.sym_alphas} value={formatValue(geoGridData.subBaseCourse.loadSpreadingAngle)} unit="°" condition={geoGridData.subBaseCourse.loadSpreadingAngle !== undefined} />
                                <PdfRow label={t('subBaseCourse.cohesion')} symbol={symbols.sym_cs} value={convertInputValue(geoGridData.subBaseCourse.cohesion, 'subbaseCohesion', 'kPa', 'psf')} unit={getUnit('kPa', 'psf')} condition={geoGridData.subBaseCourse.cohesion !== undefined} />
                            </PdfTable>
                        )}

                        {/* Subgrade Table */}
                        {geoGridData?.subgrade && (
                            <PdfTable title={t('subgrade')} t={t}>
                                <PdfRow label={t('subgrade.cbrSubgrade')} symbol="CBR" value={formatValue(geoGridData.subgrade.cbrSubgrade)} unit="%" condition={geoGridData.subgrade.subgradeInputMethod === 'cbr' && geoGridData.subgrade.cbrSubgrade !== undefined} />
                                <PdfRow label={t('subgrade.deformationModulus') || 'Deformation Modulus'} symbol={symbols.sym_ev2} value={convertInputValue(geoGridData.subgrade.deformationModulus, 'deformationModulus', 'MPa', 'psi')} unit={getUnit('MPa', 'psi')} condition={(geoGridData.subgrade.subgradeInputMethod === 'ev2' || geoGridData.subgrade.subgradeInputMethod === 'modulus') && geoGridData.subgrade.deformationModulus !== undefined} />
                                <PdfRow label={t('subgrade.safetyFactor')} symbol="FS" value={formatValue(geoGridData.subgrade.safetyFactor)} unit="-" condition={geoGridData.subgrade.safetyFactor !== undefined} />
                            </PdfTable>
                        )}
                    </View>
                </View>
            )}
            
            {/* Results Section */}
            {showCourseResults && (
                <>
                    <View style={pdfSharedStyles.spacer2} />
                    <Text style={[pdfSharedStyles.title, pdfSharedStyles.resultTitle]}>{t('results') || 'Results'}</Text>

                    {/* GeoGrid Layer Visualization */}
                    {showGeoGridImage && geogridImage && (
                        <View style={{ alignItems: 'center', width: '100%' }} wrap={false}>
                            <Image style={[pdfSharedStyles.graphicImage, { width: '100%', maxWidth: '100%' }]} src={geogridImage} />
                        </View>
                    )}
<View break />
                    {/* Base Course Results */}
                    {showCourseResults && baseThickness > 0 && baseGeogrids.length > 0 && (
                        <PdfResultTable title={t('geogrid_base_course', 'Base Course Details')} t={t}>
                            <PdfRow label={t('graphic.table.course_thickness', { course: t('geogrid_base_course') }) || `${t('geogrid_base_course')} Thickness`} symbol="t" value={convertNumericValue(baseThickness, 'm', 'ft')} unit={getUnit('m', 'ft')} />

                            {baseGeogrids.map((geogrid, index) => {
                                const geogridNum = index + 1;
                                const productFamily = geogrid.type;
                                const productName = geogrid.selectedGeogrid;
                                const designStrain = geogrid.designStrain;
                                const courseType = 'base';

                                const calculatedRequiredStrength = calculateGeogridRequiredStrengthUnified(projectGeoGridData, index, courseType);
                                const calculatedNominalStrength = getTensileAtStrain(ProductDataService.getAllProducts(), productFamily, productName, designStrain);
                                const fallbackRequiredStrength = getTensileAtStrain(ProductDataService.getAllProducts(), productFamily, productName, designStrain);

                                // Get tensile breakdown (Tz, Tp, Tm)
                                const breakdown = calculateGeogridStrengthBreakdownUnified(projectGeoGridData, index, courseType);

                                const numericReqStrength = calculatedRequiredStrength ?? fallbackRequiredStrength;
                                const numericNomStrength = calculatedNominalStrength;

                                // Convert strength values for Imperial units (kN/m → lbf/ft)
                                const displayRequiredStrength = convertNumericValue(numericReqStrength, 'kN/m', 'lbf/ft');
                                const displayNominalStrength = convertNumericValue(numericNomStrength, 'kN/m', 'lbf/ft');

                                let statusText: string;
                                if (numericNomStrength !== null && numericReqStrength !== null && numericNomStrength >= numericReqStrength) {
                                    statusText = t('graphic.table.verified', 'Verified');
                                } else if (numericNomStrength === null || numericReqStrength === null) {
                                    statusText = t('graphic.table.data_missing_for_verification', 'Data Missing for Verification');
                                } else {
                                    statusText = t('graphic.table.not_verified', 'Not Verified');
                                }
                                const isVerified = !statusText.includes('Not Verified');

                                return (
                                    <React.Fragment key={index}>
                                        <PdfRow label={t('graphic.table.geogrid_product', { num: geogridNum })} value={geogrid.selectedGeogrid || t('common.not_available', 'N/A')} colSpan={3} />
                                        <PdfRow label={t('graphic.table.geogrid_position', { num: geogridNum })} symbol={symbols.sym_pgeo(geogridNum)} value={convertNumericValue(geogrid.position, 'm', 'ft')} unit={getUnit('m', 'ft')} />
                                        <PdfRow
                                            label={t('graphic.table.geogrid_strain', { num: geogridNum })}
                                            symbol={symbols.sym_ed(geogridNum)}
                                            value={(geogrid.designStrain?.toString() || t('common.not_available', 'N/A')) ?? 'N/A'}
                                            unit="%"
                                        />
                                        {/* Tensile contribution breakdown rows */}
                                        <PdfRow label={t('graphic.table.geogrid_earth_pressure')} symbol={symbols.sym_tz(geogridNum)} value={convertNumericValue(breakdown?.Tz ?? null, 'kN/m', 'lbf/ft')} unit={getUnit('kN/m', 'lbf/ft')} />
                                        <PdfRow label={t('graphic.table.geogrid_tension_component')} symbol={symbols.sym_tp(geogridNum)} value={convertNumericValue(breakdown?.Tp ?? null, 'kN/m', 'lbf/ft')} unit={getUnit('kN/m', 'lbf/ft')} />
                                        <PdfRow label={t('graphic.table.geogrid_membrane_effect')} symbol={symbols.sym_tm(geogridNum)} value={convertNumericValue(breakdown?.Tm ?? null, 'kN/m', 'lbf/ft')} unit={getUnit('kN/m', 'lbf/ft')} />
                                        <PdfRow label={t('graphic.table.geogrid_req_strength', { num: geogridNum })} symbol={symbols.sym_treq(geogridNum)} value={displayRequiredStrength} unit={getUnit('kN/m', 'lbf/ft')} />
                                        <PdfRow label={t('graphic.table.geogrid_nom_strength', { num: geogridNum })} symbol={symbols.sym_tnom(geogridNum)} value={displayNominalStrength} unit={getUnit('kN/m', 'lbf/ft')} />
                                        <PdfStatusRow text={statusText} isVerified={isVerified} />
                                    </React.Fragment>
                                );
                            })}
                        </PdfResultTable>
                    )}

                    

                    {/* Subbase Course Results */}
                    {showCourseResults && subbaseThickness > 0 && subbaseGeogrids.length > 0 && (
                        <PdfResultTable title={t('geogrid_subbase_course', 'Subbase Course Details')} t={t}>
                            <PdfRow label={t('graphic.table.course_thickness', { course: t('geogrid_subbase_course') }) || `${t('geogrid_subbase_course')} Thickness`} symbol="t" value={convertNumericValue(subbaseThickness, 'm', 'ft')} unit={getUnit('m', 'ft')} />

                            {subbaseGeogrids.map((geogrid, index) => {
                                const geogridNum = index + 1;
                                const productFamily = geogrid.type;
                                const productName = geogrid.selectedGeogrid;
                                const designStrain = geogrid.designStrain;
                                const courseType = 'subbase';

                                const calculatedRequiredStrength = calculateGeogridRequiredStrengthUnified(projectGeoGridData, index, courseType);
                                const calculatedNominalStrength = getTensileAtStrain(ProductDataService.getAllProducts(), productFamily, productName, designStrain);
                                const fallbackRequiredStrength = getTensileAtStrain(ProductDataService.getAllProducts(), productFamily, productName, designStrain);

                                // Get tensile breakdown (Tz, Tp, Tm)
                                const breakdown = calculateGeogridStrengthBreakdownUnified(projectGeoGridData, index, courseType);

                                const numericReqStrength = calculatedRequiredStrength ?? fallbackRequiredStrength;
                                const numericNomStrength = calculatedNominalStrength;

                                // Convert strength values for Imperial units (kN/m → lbf/ft)
                                const displayRequiredStrength = convertNumericValue(numericReqStrength, 'kN/m', 'lbf/ft');
                                const displayNominalStrength = convertNumericValue(numericNomStrength, 'kN/m', 'lbf/ft');

                                let statusText: string;
                                if (numericNomStrength !== null && numericReqStrength !== null && numericNomStrength >= numericReqStrength) {
                                    statusText = t('graphic.table.verified', 'Verified');
                                } else if (numericNomStrength === null || numericReqStrength === null) {
                                    statusText = t('graphic.table.data_missing_for_verification', 'Data Missing for Verification');
                                } else {
                                    statusText = t('graphic.table.not_verified', 'Not Verified');
                                }
                                const isVerified = !statusText.includes('Not Verified');

                                return (
                                    <React.Fragment key={index}>
                                        <PdfRow label={t('graphic.table.geogrid_product', { num: geogridNum })} value={geogrid.selectedGeogrid || t('common.not_available', 'N/A')} colSpan={3} />
                                        <PdfRow label={t('graphic.table.geogrid_position', { num: geogridNum })} symbol={symbols.sym_pgeo(geogridNum)} value={convertNumericValue(geogrid.position, 'm', 'ft')} unit={getUnit('m', 'ft')} />
                                        <PdfRow label={t('graphic.table.geogrid_strain', { num: geogridNum })} symbol={symbols.sym_ed(geogridNum)} value={(geogrid.designStrain?.toString() || t('common.not_available', 'N/A')) ?? 'N/A'} unit="%" />
                                        {/* Tensile contribution breakdown rows */}
                                        <PdfRow label={t('graphic.table.geogrid_earth_pressure')} symbol={symbols.sym_tz(geogridNum)} value={convertNumericValue(breakdown?.Tz ?? null, 'kN/m', 'lbf/ft')} unit={getUnit('kN/m', 'lbf/ft')} />
                                        <PdfRow label={t('graphic.table.geogrid_tension_component')} symbol={symbols.sym_tp(geogridNum)} value={convertNumericValue(breakdown?.Tp ?? null, 'kN/m', 'lbf/ft')} unit={getUnit('kN/m', 'lbf/ft')} />
                                        <PdfRow label={t('graphic.table.geogrid_membrane_effect')} symbol={symbols.sym_tm(geogridNum)} value={convertNumericValue(breakdown?.Tm ?? null, 'kN/m', 'lbf/ft')} unit={getUnit('kN/m', 'lbf/ft')} />
                                        <PdfRow label={t('graphic.table.geogrid_req_strength', { num: geogridNum })} symbol={symbols.sym_treq(geogridNum)} value={displayRequiredStrength} unit={getUnit('kN/m', 'lbf/ft')} />
                                        <PdfRow label={t('graphic.table.geogrid_nom_strength', { num: geogridNum })} symbol={symbols.sym_tnom(geogridNum)} value={displayNominalStrength} unit={getUnit('kN/m', 'lbf/ft')} />
                                        <PdfStatusRow text={statusText} isVerified={isVerified} />
                                    </React.Fragment>
                                );
                            })}
                        </PdfResultTable>
                    )}
                </>
            )}
        </>
    );
};

interface PdfRowProps {
    label: string | any;
    symbol?: string | React.ReactElement;
    value?: string | any;
    unit?: string;
    colSpan?: number;
    condition?: boolean;
    isEven?: boolean;
}

const PdfRow: React.FC<PdfRowProps> = ({ label, symbol = '', value = '', unit = '', colSpan = 1, condition = true, isEven = false }: PdfRowProps) => {
    if (condition === false) return null;

    // PM UX 2026-04-19: auto-merge Value+Unit when unit is absent (dash/empty/undefined)
    const effectiveColSpan = colSpan === 1 && typeof unit === 'string' && isEmptyUnit(unit) ? 2 : colSpan;

    const rowStyle = [pdfSharedStyles.tableRow, isEven ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow];

    let valueWidth = '30%';
    let showSymbol = true;
    let showUnit = true;

    if (effectiveColSpan === 3) {
        valueWidth = '70%'; // 20 (Symbol) + 30 (Value) + 20 (Unit)
        showSymbol = false;
        showUnit = false;
    } else if (effectiveColSpan === 2) {
        valueWidth = '50%'; // 30 (Value) + 20 (Unit)
        showUnit = false;
    }

    return (
        <View style={rowStyle} wrap={false}>
            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol, { width: '30%' }]}>
                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{label}</Text>
            </View>

            {showSymbol && (
                <View style={[pdfSharedStyles.tableColSymbol, { width: '20%' }]}>
                    <Text style={pdfSharedStyles.tableCell}>{symbol}</Text>
                </View>
            )}

            <View style={[pdfSharedStyles.tableColValue, { width: valueWidth }]}>
                <Text style={pdfSharedStyles.tableCell}>{value}</Text>
            </View>

            {showUnit && (
                <View style={[pdfSharedStyles.tableColUnit, { width: '20%' }]}>
                    <Text style={pdfSharedStyles.tableCell}>{unit}</Text>
                </View>
            )}
        </View>
    );
};

interface PdfStatusRowProps {
    text: string;
    isVerified: boolean;
}

const PdfStatusRow: React.FC<PdfStatusRowProps> = ({ text, isVerified }) => {
    return (
        <View style={pdfSharedStyles.tableRow} wrap={false}>
            <View style={[pdfSharedStyles.tableCol, isVerified ? pdfSharedStyles.verifiedCol : pdfSharedStyles.notVerifiedCol]}>
                <Text style={{ ...pdfSharedStyles.verifiedColTableCell, textAlign: 'left', paddingLeft: 10 }}>{text}</Text>
            </View>
        </View>
    );
};

interface PdfTableProps {
    title: string;
    children: React.ReactNode;
    t: TFunction;
}

const PdfTable: React.FC<PdfTableProps> = ({ title, children, t }: PdfTableProps) => {
    const validChildren = React.Children.toArray(children).filter((child: React.ReactNode): child is React.ReactElement => React.isValidElement(child));
    if (validChildren.length === 0) return null;

    return (
        <View style={pdfSharedStyles.colHalf}>
            <Text style={pdfSharedStyles.tableTitle}>{title}</Text>
            <View style={pdfSharedStyles.spacer0} />
            <View style={pdfSharedStyles.table}>
                <View style={pdfSharedStyles.tableRow}>
                    <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol, { width: '30%' }]}>
                        <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                    </View>
                    <View style={[pdfSharedStyles.tableColHeaderSymbol, { width: '20%' }]}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                    </View>
                    <View style={[pdfSharedStyles.tableColHeaderValue, { width: '30%' }]}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                    </View>
                    <View style={[pdfSharedStyles.tableColHeaderUnit, { width: '20%' }]}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                    </View>
                </View>
                {validChildren.map((child: React.ReactElement, idx: number) => {
                    if (React.isValidElement(child) && child.type === PdfRow) {
                        return React.cloneElement(child as React.ReactElement<PdfRowProps>, { isEven: idx % 2 === 0 });
                    }
                    return child;
                })}
            </View>
        </View>
    );
}

interface PdfResultTableProps {
    title: string;
    children: React.ReactNode;
    t: TFunction;
}

const PdfResultTable: React.FC<PdfResultTableProps> = ({ title, children, t }) => {
    const validChildren = React.Children.toArray(children).filter((child: React.ReactNode): child is React.ReactElement => React.isValidElement(child));
    if (validChildren.length === 0) return null;

    return (
        <View style={pdfSharedStyles.section}>
            <View style={pdfSharedStyles.spacer2} />
            <Text style={pdfSharedStyles.tableTitle}>{title}</Text>
            <View style={pdfSharedStyles.spacer0} />
            <View style={pdfSharedStyles.table}>
                <View style={pdfSharedStyles.tableRow} fixed>
                    <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol, { width: '30%' }]}>
                        <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                    </View>
                    <View style={[pdfSharedStyles.tableColHeaderSymbol, { width: '20%' }]}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                    </View>
                    <View style={[pdfSharedStyles.tableColHeaderValue, { width: '30%' }]}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                    </View>
                    <View style={[pdfSharedStyles.tableColHeaderUnit, { width: '20%' }]}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                    </View>
                </View>
                {validChildren.map((child: React.ReactElement, idx: number) => {
                    if (React.isValidElement(child) && child.type === PdfRow) {
                        return React.cloneElement(child as React.ReactElement<PdfRowProps>, { isEven: idx % 2 === 0 });
                    }
                    return child;
                })}
            </View>
        </View>
    );
};
